<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useVehicleStore } from './stores/vehicleStore'
import { useNavigationStore } from './stores/navigationStore'
import { usePartsStore } from './stores/partsStore'
import { useUrlState } from './composables/useUrlState'
import SearchPanel from './components/search/SearchPanel.vue'
import VehicleInfo from './components/vehicle/VehicleInfo.vue'
import BodyTypeSelector from './components/vehicle/BodyTypeSelector.vue'
import BreadcrumbNav from './components/navigation/BreadcrumbNav.vue'
import CategoryMenu from './components/navigation/CategoryMenu.vue'
import SvgViewer from './components/content/SvgViewer.vue'
import PartsList from './components/content/PartsList.vue'
import CategoryGrid from './components/content/CategoryGrid.vue'
import CustomRmiLinksBar from './components/content/CustomRmiLinksBar.vue'
import CustomCategoryPickerModal from './components/content/CustomCategoryPickerModal.vue'
import HideCategoryModal from './components/content/HideCategoryModal.vue'
import SkeletonLoader from './components/shared/SkeletonLoader.vue'
import ErrorDisplay from './components/shared/ErrorDisplay.vue'
import TecDocProductEnrichment from './components/product/TecDocProductEnrichment.vue'
import type { Category } from './types'
import type { CustomRmiLink } from './api/tecdocApi'
import * as api from './api/tecdocApi'

const props = defineProps<{
  mode: 'catalog' | 'product'
  sku?: string
}>()

const vehicleStore = useVehicleStore()
const navStore = useNavigationStore()
const partsStore = usePartsStore()

if (props.mode === 'catalog') {
  useUrlState()
}

// Fetch admin config once on mount. Tells the UI whether the current user
// can edit category names (TecdocEditor role), and supplies pinned
// manufacturers + custom category name overrides.
navStore.loadAdminConfig()

const hasVehicle = computed(() => vehicleStore.carId !== null)
const showSearch = computed(() => !hasVehicle.value)
const oemHasSearched = computed(() =>
  vehicleStore.searchMode === 'oem' && (partsStore.articles.length > 0 || partsStore.isLoadingParts || partsStore.partsError !== null)
)
const showRmiNav = computed(() =>
  hasVehicle.value && vehicleStore.hasGraphics && !vehicleStore.showBodyTypeSelector
)
const showStandardNav = computed(() =>
  hasVehicle.value && !vehicleStore.hasGraphics && !vehicleStore.showBodyTypeSelector
)

// Current SVG content based on navigation level
const currentSvg = computed(() => {
  switch (navStore.currentLevel) {
    case 'subGroup': return navStore.subGroupSvg
    case 'mainGroup': return navStore.mainGroupSvg
    case 'vehicle':
    default: return navStore.vehicleSvg
  }
})

const svgLevel = computed(() => navStore.currentLevel === 'products' ? 'subGroup' : navStore.currentLevel)

// Auto-load vehicle image when vehicle selected with RMI graphics
watch(
  () => [vehicleStore.carId, vehicleStore.qualId, vehicleStore.hasGraphics, vehicleStore.showBodyTypeSelector],
  ([carId, qualId, hasGraphics, showBody]) => {
    if (carId && hasGraphics && !showBody && qualId !== null && qualId !== undefined) {
      navStore.loadVehicleImage(String(qualId), carId as number)
    }
  },
  { immediate: true }
)

// Standard categories state
const standardCategories = ref<Category[]>([])
const isLoadingCategories = ref(false)
const standardCatPath = ref<number[]>([]) // breadcrumb of catIds

// Auto-load standard categories when vehicle selected without RMI
watch(
  () => [vehicleStore.carId, vehicleStore.hasGraphics, vehicleStore.showBodyTypeSelector],
  async ([carId, hasGraphics, showBody]) => {
    if (carId && !hasGraphics && !showBody) {
      isLoadingCategories.value = true
      try {
        standardCategories.value = await api.getCategories(carId as number, 0)
        standardCatPath.value = []
      } catch { }
      finally { isLoadingCategories.value = false }
    }
  },
  { immediate: true }
)

// --- Event handlers ---

function changeVehicle() {
  vehicleStore.reset()
  navStore.reset()
  partsStore.reset()
}

function onSvgAreaClicked(id: string, name: string) {
  if (!vehicleStore.carId || vehicleStore.qualId === null || vehicleStore.qualId === undefined) return

  const qualId = String(vehicleStore.qualId)
  const carId = vehicleStore.carId

  if (id.startsWith('h')) {
    const mgId = id.substring(1)
    navStore.navigateToMainGroup(qualId, carId, mgId, name)
  } else if (id.startsWith('s')) {
    const sgId = id.substring(1)
    if (navStore.mainGroupId) {
      navStore.navigateToSubGroup(qualId, carId, navStore.mainGroupId, sgId, name)
    }
  } else if (id.startsWith('c')) {
    const itemId = id.substring(1)
    navStore.setProductsLevel(itemId, name)
    partsStore.loadRmiParts(
      qualId, carId,
      navStore.mainGroupId ?? '0',
      navStore.subGroupId ?? '0',
      Number(itemId)
    )
  }
}

function onCategoryMainGroup(mgId: number, name: string) {
  if (!vehicleStore.carId || vehicleStore.qualId === null) return
  navStore.navigateToMainGroup(String(vehicleStore.qualId), vehicleStore.carId, String(mgId), name)
}

function onCategorySubGroup(mgId: number, sgId: number, name: string) {
  if (!vehicleStore.carId || vehicleStore.qualId === null) return
  navStore.navigateToSubGroup(
    String(vehicleStore.qualId), vehicleStore.carId,
    String(mgId), String(sgId), name
  )
}

function onCategoryItem(mgId: number, sgId: number, itemMpId: number, name: string) {
  if (!vehicleStore.carId || vehicleStore.qualId === null) return
  navStore.setProductsLevel(String(itemMpId), name)
  partsStore.loadRmiParts(
    String(vehicleStore.qualId), vehicleStore.carId,
    String(mgId), String(sgId), itemMpId
  )
}

// Custom-RMI-links picker modal (lifted up from CustomRmiLinksBar so both
// the chip bar and the sidebar category menu can open the same modal).
const showCustomLinkPicker = ref(false)
const customLinkPickerCarId = computed<number | null>(() =>
  vehicleStore.carDetails?.carId ?? vehicleStore.carId ?? null
)
const customLinkError = ref<string | null>(null)
// Scope captured at the moment the editor clicks "+ Legg til" — the saved link
// will only appear when the user navigates back to this exact (mg, sg) pair.
const pendingLinkScope = ref<{ mg: string | null; sg: string | null }>({ mg: null, sg: null })

function openCustomLinkPicker() {
  customLinkError.value = null
  pendingLinkScope.value = {
    mg: navStore.mainGroupId ?? null,
    sg: navStore.subGroupId ?? null,
  }
  if (customLinkPickerCarId.value) showCustomLinkPicker.value = true
}

function closeCustomLinkPicker() {
  showCustomLinkPicker.value = false
}

async function onCustomLinkPickerSave(payload: { categoryId: number; label: string; iconCode: string }) {
  customLinkError.value = null
  try {
    await navStore.addCustomRmiLink(
      payload.categoryId,
      payload.label,
      pendingLinkScope.value.mg,
      pendingLinkScope.value.sg,
      payload.iconCode,
    )
    showCustomLinkPicker.value = false
  } catch (err) {
    customLinkError.value = (err as Error).message || 'Kunne ikke lagre'
  }
}

// Hide/show category modal
const showHideCategoryModal = ref(false)
const hideCategoryTarget = ref<{ id: string; label: string } | null>(null)

function openHideCategoryModal(id: string, label: string) {
  hideCategoryTarget.value = { id, label }
  showHideCategoryModal.value = true
}

function closeHideCategoryModal() {
  showHideCategoryModal.value = false
  hideCategoryTarget.value = null
}

async function onHideCategoryConfirm(payload: { id: string; label: string; mode: 'whitebox' | 'opacity' }) {
  try {
    await navStore.hideCategory(payload.id, payload.label, payload.mode)
    showHideCategoryModal.value = false
    hideCategoryTarget.value = null
  } catch (err) {
    alert('Kunne ikke skjule: ' + ((err as Error).message || ''))
  }
}

async function onUnhideCategory(id: string) {
  try {
    await navStore.unhideCategory(id)
  } catch (err) {
    alert('Kunne ikke gjenopprette: ' + ((err as Error).message || ''))
  }
}

async function onCustomRmiLinkNavigate(link: CustomRmiLink) {
  // Use the real TecDoc carId for the non-RMI parts endpoint, falling back
  // to vehicleStore.carId when there are no graphics (where they're the same).
  const catCarId = vehicleStore.carDetails?.carId ?? vehicleStore.carId
  if (!catCarId) return
  // Jump straight to the non-RMI parts list for this assembly group id.
  // Clear mid-tree state because the custom link isn't part of the RMI drilldown.
  navStore.mainGroupId = null
  navStore.subGroupId = null
  navStore.mainGroupSvg = null
  navStore.subGroupSvg = null
  navStore.itemId = null
  navStore.currentLevel = 'products'
  navStore.navigationPath = [
    navStore.navigationPath[0] ?? { level: 'vehicle', id: String(catCarId), name: 'Kjøretøy' },
    { level: 'products', id: String(link.categoryId), name: link.label },
  ]
  await partsStore.loadCategoryParts(catCarId, link.categoryId)
}

async function onStandardCategorySelect(catId: number, hasChildren: boolean) {
  if (!vehicleStore.carId) return
  if (hasChildren) {
    // Drill into sub-categories
    isLoadingCategories.value = true
    try {
      standardCatPath.value.push(catId)
      standardCategories.value = await api.getCategories(vehicleStore.carId, catId)
    } catch { }
    finally { isLoadingCategories.value = false }
  } else {
    await partsStore.loadCategoryParts(vehicleStore.carId, catId)
  }
}
</script>

<template>
  <div class="tecdoc-vue-app">
    <template v-if="mode === 'catalog'">
      <div class="tecdoc-catalog">
        <!-- Search panel -->
        <SearchPanel v-if="showSearch" />

        <!-- OEM search results (no vehicle needed) -->
        <template v-if="oemHasSearched && !hasVehicle">
          <PartsList
            :articles="partsStore.articles"
            :is-loading="partsStore.isLoadingParts"
            :error="partsStore.partsError"
            @retry="() => {}"
          />
        </template>

        <!-- Vehicle selected -->
        <template v-if="hasVehicle">
          <VehicleInfo @change-vehicle="changeVehicle" />
          <BodyTypeSelector />

          <!-- RMI graphical navigation -->
          <template v-if="showRmiNav">
            <BreadcrumbNav />

            <div class="tecdoc-content">
              <div class="tecdoc-content__sidebar">
                <CategoryMenu
                  :categories="navStore.categories"
                  @select-main-group="onCategoryMainGroup"
                  @select-sub-group="onCategorySubGroup"
                  @select-item="onCategoryItem"
                  @select-custom-link="onCustomRmiLinkNavigate"
                  @add-custom-link-requested="openCustomLinkPicker"
                  @hide-category-requested="openHideCategoryModal"
                  @unhide-category-requested="onUnhideCategory"
                />
              </div>

              <div class="tecdoc-content__main">
                <!-- Editor-pinned custom category shortcuts (above the SVG) -->
                <CustomRmiLinksBar
                  v-if="navStore.currentLevel !== 'products'"
                  @navigate="onCustomRmiLinkNavigate"
                  @add-clicked="openCustomLinkPicker"
                />

                <!-- SVG viewer (when not showing products) -->
                <SvgViewer
                  v-if="navStore.currentLevel !== 'products'"
                  :svg-content="currentSvg"
                  :is-loading="navStore.isLoadingSvg"
                  :level="svgLevel"
                  :hidden-ids="navStore.hiddenIds"
                  :hidden-items="navStore.hiddenItems"
                  @area-clicked="onSvgAreaClicked"
                  @area-hovered="() => {}"
                  @area-left="() => {}"
                />

                <!-- Parts list (when products level) -->
                <PartsList
                  v-if="navStore.currentLevel === 'products'"
                  :articles="partsStore.articles"
                  :is-loading="partsStore.isLoadingParts"
                  :error="partsStore.partsError"
                  @retry="() => {}"
                />

                <!-- Navigation error -->
                <ErrorDisplay
                  v-if="navStore.navigationError"
                  :message="navStore.navigationError"
                  :retryable="true"
                  @retry="navStore.navigationError = null"
                />
              </div>
            </div>
          </template>

          <!-- Custom RMI link picker modal (shared between chip bar and sidebar) -->
          <CustomCategoryPickerModal
            v-if="showCustomLinkPicker && customLinkPickerCarId"
            :car-id="customLinkPickerCarId"
            @save="onCustomLinkPickerSave"
            @close="closeCustomLinkPicker"
          />

          <!-- Hide category modal (TecdocEditor only) -->
          <HideCategoryModal
            v-if="showHideCategoryModal && hideCategoryTarget"
            :category-id="hideCategoryTarget.id"
            :category-label="hideCategoryTarget.label"
            @confirm="onHideCategoryConfirm"
            @close="closeHideCategoryModal"
          />

          <!-- Standard category navigation (no RMI) -->
          <template v-if="showStandardNav">
            <div class="tecdoc-content">
              <div class="tecdoc-content__sidebar">
                <CategoryGrid
                  :categories="standardCategories"
                  :is-loading="isLoadingCategories"
                  @select-category="onStandardCategorySelect"
                />
              </div>
              <div class="tecdoc-content__main">
                <PartsList
                  v-if="partsStore.articles.length > 0"
                  :articles="partsStore.articles"
                  :is-loading="partsStore.isLoadingParts"
                  :error="partsStore.partsError"
                  @retry="() => {}"
                />
                <div v-else class="tecdoc-content__placeholder">
                  Velg en kategori for å se deler
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </template>

    <!-- Product enrichment mode -->
    <template v-else-if="mode === 'product'">
      <TecDocProductEnrichment v-if="sku" :sku="sku" />
    </template>
  </div>
</template>

<style scoped lang="scss">
.tecdoc-catalog {
  padding: 1rem 0;
}

.tecdoc-content {
  display: flex;
  gap: 1.5rem;
  margin-top: 1rem;

  &__sidebar {
    flex: 0 0 380px;
    min-width: 180px;
  }

  &__main {
    flex: 1;
    min-width: 0;

    :deep(.svg-viewer) {
      width: 80vh;
      max-width: 100%;
    }

    :deep(.parts-list) {
      max-width: 80%;
    }
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    background: #f9fafb;
    border-radius: 8px;
    color: #888;
  }
}

@media (max-width: 768px) {
  .tecdoc-content {
    flex-direction: column;

    &__sidebar {
      flex: none;
      width: 100%;
    }

    &__main {
      :deep(.svg-viewer),
      :deep(.parts-list) {
        max-width: 100%;
      }
    }
  }
}
</style>
